import { formatCodeableConcept, type ProfileResource } from '@medplum/core'
import type {
  Address,
  ClientApplication,
  CodeableConcept,
  ContactPoint,
  HumanName,
  Reference,
  Resource,
} from '@medplum/fhirtypes'
import { match } from 'ts-pattern'
import { z } from 'zod'
import {
  codeableConcept,
  codeFromCodeableConcept,
} from '#src/codeableConcept.ts'
import {
  LANGUAGE_SYSTEM,
  NPI_SYSTEM,
  PROFILE_TAG_SYSTEM,
} from '#src/codeSystems.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import * as contactInfo from '#src/models/contactInfo.ts'
import {
  getPrimaryEmail,
  getPrimaryFaxNumber,
  getPrimaryPhoneNumber,
  homeAddressOrFallback,
} from '#src/models/patients/patients.ts'
import { phoneSearchVariants } from '#src/phone.ts'
import type { QueryTypes } from '#src/search.ts'
import {
  capitalize,
  type FhirValue,
  getHumanName,
  getNickname,
  humanNameToLastFirstMiddleInitialString,
  humanNameToString,
  isOfType,
  officialNameOrFallback,
  setHumanName,
} from '#src/utils.ts'

interface LanguageNames {
  [languageCode: string]: string
}

const customMapping: LanguageNames = {
  'zh-HK': 'Cantonese', // default Intl value is "Chinese (Hong Kong)"
  'zh-CN': 'Mandarin', // default Intl value is "Chinese (China)"
  'es-MX': 'Spanish', // default Intl value is "Mexican Spanish"
  'en-US': 'English',
}

export const languageOptions = Object.entries(customMapping).map(
  ([code, display]) => ({
    value: code,
    label: display,
    preferred: false,
  })
)
export interface ProfileLanguageCode {
  code: string
  display: string
  preferred: boolean
}

export interface AddressSummary {
  line?: string[]
  city?: string
  state?: string
  postalCode?: string
}

export abstract class ExtendedProfile<
  T extends ProfileResource,
> extends ExtendedResource<T> {
  static byPhoneNumber(phoneNumber: string): QueryTypes {
    return {
      phone: phoneSearchVariants(phoneNumber),
    }
  }

  static byFaxNumber(faxNumber: string): QueryTypes {
    return {
      telecom: phoneSearchVariants(faxNumber),
    }
  }

  static byNPI(npi: string): QueryTypes {
    return {
      identifier: { system: NPI_SYSTEM, value: npi },
    }
  }

  static byName(name: string): QueryTypes {
    return {
      // Strip commas, as the server will treat them as OR conditions
      name: name.replace(/,/g, ''),
    }
  }

  get name(): string {
    if (this.isDeleted) {
      return '[deleted]'
    }
    const humanName = officialNameOrFallback(this.resource)
    return humanName ? humanNameToString(humanName) : 'Unknown'
  }

  // "Smith, John R." — like `name` but with middle names abbreviated.
  get nameLastFirstMiddleInitial(): string {
    if (this.isDeleted) {
      return '[deleted]'
    }
    const humanName = officialNameOrFallback(this.resource)
    return humanName
      ? humanNameToLastFirstMiddleInitialString(humanName)
      : 'Unknown'
  }

  get firstName(): string {
    if (this.isDeleted) {
      return '[deleted]'
    }
    const humanName = getHumanName(this.resource, {
      use: ['usual', 'official'],
      fallback: true,
    })
    return humanName?.given?.join(' ') ?? 'Unknown'
  }

  set firstName(value: string) {
    const current = getHumanName(this.resource, {
      use: ['usual', 'official'],
      fallback: true,
    })
    this.setName({
      ...current,
      use: current ? current.use : 'official',
      given: value ? [value] : undefined,
    })
  }

  get firstNamePossessive(): string {
    const firstName = this.firstName
    return firstName.endsWith('s') ? `${firstName}'` : `${firstName}'s`
  }

  get lastName(): string {
    if (this.isDeleted) {
      return '[deleted]'
    }
    const humanName = getHumanName(this.resource, {
      use: ['usual', 'official'],
      fallback: true,
    })
    return humanName?.family ?? 'Unknown'
  }

  set lastName(value: string) {
    const current = getHumanName(this.resource, {
      use: ['usual', 'official'],
      fallback: true,
    })
    this.setName({
      ...current,
      use: current ? current.use : 'official',
      family: value || undefined,
    })
  }

  get lastNameOfficialOrFallback(): string | undefined {
    if (this.isDeleted) {
      return '[deleted]'
    }
    return officialNameOrFallback(this.resource)?.family
  }

  set lastNameOfficialOrFallback(value: string | undefined) {
    const current = officialNameOrFallback(this.resource)
    this.setName({
      ...current,
      use: current ? current.use : 'official',
      family: value || undefined,
    })
  }

  get firstNameOfficialOrFallback(): string | undefined {
    if (this.isDeleted) {
      return '[deleted]'
    }
    return officialNameOrFallback(this.resource)?.given?.join(' ')
  }

  set firstNameOfficialOrFallback(value: string | undefined) {
    const current = officialNameOrFallback(this.resource)
    this.setName({
      ...current,
      use: current ? current.use : 'official',
      given: value ? [value] : undefined,
    })
  }

  get nickname(): string | undefined {
    if (this.isDeleted) {
      return '[deleted]'
    }
    return getNickname(this.resource)
  }

  get initials(): string {
    const initials = this.firstName[0] + this.lastName[0]
    return initials.toUpperCase()
  }

  get names(): HumanName[] | undefined {
    return this.resource.name
  }

  set names(value: HumanName[]) {
    this.resource.name = value
  }

  addName(name: HumanName): void {
    this.resource.name ??= []
    this.resource.name.push(name)
  }

  /**
   * Sets the name on the profile, overwriting any existing names with the same use.
   * @param name - The name to set.
   */
  setName(name: HumanName): void {
    this.resource.name ??= []
    this.resource.name = setHumanName(this.resource.name, name)
  }

  getName(use: HumanName['use']): HumanName | undefined {
    return this.resource.name?.find((n) => n.use === use)
  }

  get primaryEmail(): string | undefined {
    return getPrimaryEmail(this.resource)
  }

  setSingleEmail(email: string): void {
    this.removeEmails()
    this.addEmail('work', email)
  }

  removeEmails(): void {
    const nonEmailTelecom =
      this.resource.telecom?.filter((t) => t.system !== 'email') ?? []

    // Avoid empty array validation error
    this.resource.telecom =
      nonEmailTelecom.length > 0 ? nonEmailTelecom : undefined
  }

  addEmail(use: ContactPoint['use'], email: string | undefined): void {
    if (!email || !this.isValidEmail(email)) {
      return
    }

    this.resource.telecom ??= []

    if (this.resource.telecom.length > 0) {
      this.resource.telecom = this.resource.telecom.filter(
        (t) => !(t.system === 'email' && t.value === email)
      )
    }

    this.resource.telecom.push({
      system: 'email',
      value: email,
      use,
    })
  }

  private isValidEmail(email?: string): boolean {
    if (!email) {
      return false
    }
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)
  }

  get primaryPhoneNumber(): string | undefined {
    return getPrimaryPhoneNumber(this.resource)
  }

  get primaryFaxNumber(): string | undefined {
    return getPrimaryFaxNumber(this.resource)
  }

  get primaryAddress(): Address | undefined {
    return homeAddressOrFallback(this.resource)
  }

  set address(address: Address[]) {
    this.resource.address = address
  }

  get address(): Address[] {
    return this.resource.address ?? []
  }

  addAddress(address: Address): void {
    const existing = (this.resource.address ?? []).filter(
      (a) => a.use !== address.use
    )
    this.resource.address = [...existing, address]
  }

  get contactMethods(): ContactPoint[] {
    return contactInfo.getContactMethods(this.resource)
  }

  set contactMethods(methods: ContactPoint[]) {
    contactInfo.setContactMethods(this.resource, methods)
  }

  get addresses(): Address[] {
    return this.resource.address ?? []
  }

  set addresses(addresses: Address[]) {
    // Empty FHIR arrays fail validation; clear the field instead.
    this.resource.address = addresses.length > 0 ? addresses : undefined
  }

  get addressSummary(): AddressSummary {
    return {
      line: this.primaryAddress?.line,
      city: this.primaryAddress?.city,
      state: this.primaryAddress?.state,
      postalCode: this.primaryAddress?.postalCode,
    }
  }

  get photoUrl(): string | undefined {
    return this.resource.photo?.[0]?.url
  }

  get languages(): { language: CodeableConcept; preferred: boolean }[] {
    if (this.resource.resourceType === 'Practitioner') {
      const [preferred, ...others] = this.resource.communication ?? []
      if (!preferred) {
        return []
      }
      return [
        { language: preferred, preferred: true },
        ...others.map((l) => ({ language: l, preferred: false })),
      ]
    }
    return (this.resource.communication ?? []).map((c) => ({
      language: c.language,
      preferred: c.preferred ?? false,
    }))
  }

  get preferredLanguageOrFallback(): string | undefined {
    const languages = this.languages
    return (
      codeFromCodeableConcept(languages.find((l) => l.preferred)?.language) ??
      codeFromCodeableConcept(languages[0]?.language)
    )
  }

  get preferredLanguageDisplay(): string {
    return getLanguageDisplay(this.preferredLanguageOrFallback)
  }

  get languageCodes(): ProfileLanguageCode[] {
    return this.languages.map((l) => ({
      code: codeFromCodeableConcept(l.language) ?? 'missing-code',
      display: formatCodeableConcept(l.language) ?? 'missing-display',
      preferred: l.preferred,
    }))
  }

  set languageCodes(value: ProfileLanguageCode[]) {
    if (value.length === 0) {
      // Empty FHIR arrays fail validation; clear the field instead.
      this.resource.communication = undefined
    } else if (this.resource.resourceType === 'Practitioner') {
      // The getter encodes the preferred language as index 0.
      const preferred = value.find((l) => l.preferred)
      const ordered = preferred
        ? [preferred, ...value.filter((l) => l !== preferred)]
        : value
      this.resource.communication = ordered.map((l) => getLanguage(l.code))
    } else {
      this.resource.communication = value.map((l) => ({
        language: getLanguage(l.code),
        preferred: l.preferred,
      }))
    }
  }

  get gender(): Gender {
    return this.resource.gender ?? 'unknown'
  }

  set gender(value: Gender) {
    this.resource.gender = value
  }

  get genderInitial(): 'M' | 'F' | 'O' | '?' {
    return match(this.gender)
      .with('male', () => 'M' as const)
      .with('female', () => 'F' as const)
      .with('other', () => 'O' as const)
      .with('unknown', () => '?' as const)
      .exhaustive()
  }

  get telecomSummary(): {
    system?: string
    value?: string
    use?: string
    rank?: number
  }[] {
    return (
      this.resource.telecom?.map((t) => ({
        system: t.system,
        value: t.value,
        use: t.use,
        rank: t.rank,
      })) ?? []
    )
  }

  static readonly DELETED_TAG = {
    system: PROFILE_TAG_SYSTEM,
    code: 'DELETED',
    display: 'Deleted',
  } as const

  delete(): void {
    this.addTag(ExtendedProfile.DELETED_TAG)
    this.resource.active = false
  }

  get isDeleted(): boolean {
    return this.hasTag(ExtendedProfile.DELETED_TAG)
  }
}

export function isProfileResource(
  value: FhirValue | undefined
): value is FhirValue<'Patient' | 'Practitioner' | 'RelatedPerson'> {
  return (
    isOfType(value, 'Patient') ||
    isOfType(value, 'Practitioner') ||
    isOfType(value, 'RelatedPerson')
  )
}

export function getLanguageDisplay(languageCode: string | undefined): string {
  if (!languageCode) {
    return 'Other'
  }
  if (customMapping[languageCode]) {
    return customMapping[languageCode]
  }
  let display: string

  const displayValue = new Intl.DisplayNames(['en'], { type: 'language' })
  try {
    display = languageCode
      ? (displayValue.of(languageCode) ?? 'Unknown')
      : 'Other'
  } catch {
    display = 'Unknown'
  }

  return display
}

export function getLanguage(languageCode?: string): CodeableConcept {
  return codeableConcept({
    system: LANGUAGE_SYSTEM,
    code: languageCode ?? 'other',
    display: getLanguageDisplay(languageCode),
  })
}

export type AvatarResource =
  | ProfileResource
  | Reference<ProfileResource>
  | ClientApplication
  | Reference<ClientApplication>

export const isAvatarResource = (
  resource: Resource | Reference | undefined
): resource is AvatarResource =>
  isProfileResource(resource) || isOfType(resource, 'ClientApplication')

export const genderSchema = z.enum(['male', 'female', 'other', 'unknown'])
export type Gender = z.infer<typeof genderSchema>

export function getGender(gender?: string): Gender | undefined {
  return genderSchema.safeParse(gender)?.data
}

export const genderOptions = genderSchema.options.map((value) => ({
  value,
  label: capitalize(value),
}))
